import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Thermometer,
  Layers,
  Clock,
  Play,
  Pause,
  Square,
  RefreshCw,
  Wifi,
  WifiOff,
  AlertTriangle,
  Camera,
  ChevronDown,
  ChevronUp,
  Gauge,
} from 'lucide-react'
import type { IPrinterConnection } from '@/lib/printerConnection.types'
import { usePrinterConnectionStore } from '@/stores/printerConnectionStore'

interface PrinterMonitorProps {
  printerId: string
  compact?: boolean
}

export default function PrinterMonitor({ printerId, compact = false }: PrinterMonitorProps) {
  const [expanded, setExpanded] = useState(!compact)
  const [showCamera, setShowCamera] = useState(false)
  
  const printerStates = usePrinterConnectionStore((s) => s.printerStates)
  const activeConnections = usePrinterConnectionStore((s) => s.activeConnections)
  const { pausePrint, resumePrint, cancelPrint, reconnectPrinter } = usePrinterConnectionStore()

  const state = printerStates.get(printerId)
  const connection = activeConnections.get(printerId)

  if (!state) {
    return (
      <div className="p-4 rounded-lg bg-surface-800 border border-surface-700">
        <p className="text-surface-400 text-sm">Printer not found</p>
      </div>
    )
  }

  const { config, connectionStatus, printerStatus, telemetry, currentFile, capabilities } = state

  const isConnected = connectionStatus === 'connected'
  const isPrinting = printerStatus === 'printing'
  const isPaused = printerStatus === 'paused'

  const formatTime = (seconds?: number) => {
    if (!seconds) return '--:--'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m ${s}s`
  }

  const getStatusColor = () => {
    switch (printerStatus) {
      case 'printing':
        return 'text-green-400'
      case 'paused':
        return 'text-yellow-400'
      case 'error':
        return 'text-red-400'
      case 'heating':
        return 'text-orange-400'
      case 'idle':
        return 'text-blue-400'
      default:
        return 'text-surface-400'
    }
  }

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="w-4 h-4 text-green-400" />
      case 'connecting':
      case 'reconnecting':
        return <RefreshCw className="w-4 h-4 text-yellow-400 animate-spin" />
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-400" />
      default:
        return <WifiOff className="w-4 h-4 text-surface-500" />
    }
  }

  return (
    <motion.div
      layout
      className="rounded-xl bg-surface-900 border border-surface-700 overflow-hidden"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {getConnectionIcon()}
          <div>
            <h3 className="font-medium text-white">{config.name}</h3>
            <p className={`text-sm capitalize ${getStatusColor()}`}>
              {printerStatus}
              {currentFile && isPrinting && (
                <span className="text-surface-400 ml-2">• {currentFile}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {telemetry?.printProgress !== undefined && isPrinting && (
            <div className="text-right">
              <span className="text-lg font-semibold text-white">
                {Math.round(telemetry.printProgress)}%
              </span>
            </div>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-surface-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-surface-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="border-t border-surface-700"
        >
          {/* Progress Bar */}
          {(isPrinting || isPaused) && telemetry?.printProgress !== undefined && (
            <div className="px-4 pt-4">
              <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary-500 to-primary-400"
                  initial={{ width: 0 }}
                  animate={{ width: `${telemetry.printProgress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <div className="flex justify-between mt-1 text-xs text-surface-400">
                <span>Layer {telemetry.layerInfo?.current || '-'} / {telemetry.layerInfo?.total || '-'}</span>
                <span>{formatTime(telemetry.printTimeRemaining)} remaining</span>
              </div>
            </div>
          )}

          {/* Temperature Display */}
          <div className="grid grid-cols-2 gap-4 p-4">
            <TemperatureCard
              label="Nozzle"
              actual={telemetry?.temperatures.nozzle.actual}
              target={telemetry?.temperatures.nozzle.target}
              max={capabilities?.maxNozzleTemp || 300}
            />
            <TemperatureCard
              label="Bed"
              actual={telemetry?.temperatures.bed.actual}
              target={telemetry?.temperatures.bed.target}
              max={capabilities?.maxBedTemp || 120}
            />
            {telemetry?.temperatures.chamber && (
              <TemperatureCard
                label="Chamber"
                actual={telemetry.temperatures.chamber.actual}
                target={telemetry.temperatures.chamber.target}
                max={60}
              />
            )}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-2 px-4 pb-4">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-800">
              <Clock className="w-4 h-4 text-surface-400" />
              <div>
                <p className="text-xs text-surface-400">Elapsed</p>
                <p className="text-sm font-medium text-white">
                  {formatTime(telemetry?.printTimeElapsed)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-800">
              <Layers className="w-4 h-4 text-surface-400" />
              <div>
                <p className="text-xs text-surface-400">Layer</p>
                <p className="text-sm font-medium text-white">
                  {telemetry?.layerInfo?.current || '-'} / {telemetry?.layerInfo?.total || '-'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-surface-800">
              <Gauge className="w-4 h-4 text-surface-400" />
              <div>
                <p className="text-xs text-surface-400">Fan</p>
                <p className="text-sm font-medium text-white">
                  {telemetry?.fanSpeed !== undefined ? `${Math.round(telemetry.fanSpeed)}%` : '--'}
                </p>
              </div>
            </div>
          </div>

          {/* Camera Preview */}
          {capabilities?.hasCamera && (
            <div className="px-4 pb-4">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowCamera(!showCamera)
                }}
                className="flex items-center gap-2 text-sm text-surface-400 hover:text-white transition-colors"
              >
                <Camera className="w-4 h-4" />
                {showCamera ? 'Hide Camera' : 'Show Camera'}
              </button>
              {showCamera && (
                <div className="mt-2 aspect-video bg-surface-800 rounded-lg flex items-center justify-center">
                  <CameraPreview connection={connection} />
                </div>
              )}
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex items-center gap-2 p-4 border-t border-surface-700 bg-surface-800/50">
            {!isConnected ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  reconnectPrinter(printerId)
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reconnect
              </button>
            ) : (
              <>
                {isPrinting && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      pausePrint(printerId)
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium transition-colors"
                  >
                    <Pause className="w-4 h-4" />
                    Pause
                  </button>
                )}
                {isPaused && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      resumePrint(printerId)
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    Resume
                  </button>
                )}
                {(isPrinting || isPaused) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (confirm('Are you sure you want to cancel this print?')) {
                        cancelPrint(printerId)
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
                  >
                    <Square className="w-4 h-4" />
                    Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}

function TemperatureCard({
  label,
  actual,
  target,
  max,
}: {
  label: string
  actual?: number
  target?: number
  max: number
}) {
  const percentage = actual ? (actual / max) * 100 : 0
  const isHeating = target && target > 0 && actual && actual < target

  return (
    <div className="p-3 rounded-lg bg-surface-800">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Thermometer className={`w-4 h-4 ${isHeating ? 'text-orange-400' : 'text-surface-400'}`} />
          <span className="text-sm text-surface-300">{label}</span>
        </div>
        <span className="text-lg font-semibold text-white">
          {actual !== undefined ? `${Math.round(actual)}°` : '--'}
        </span>
      </div>
      <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${
            isHeating ? 'bg-orange-500' : percentage > 80 ? 'bg-red-500' : 'bg-primary-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      {target !== undefined && target > 0 && (
        <p className="text-xs text-surface-400 mt-1">Target: {target}°C</p>
      )}
    </div>
  )
}

function CameraPreview({
  connection,
}: {
  connection?: IPrinterConnection
}) {
  const [streamUrl, setStreamUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (connection) {
      connection
        .getCameraStream()
        .then((stream) => {
          if (stream) {
            setStreamUrl(stream.url)
          } else {
            setError('Camera not available')
          }
        })
        .catch(() => setError('Failed to get camera stream'))
    }
  }, [connection])

  if (error) {
    return (
      <div className="text-surface-400 text-sm flex items-center gap-2">
        <Camera className="w-5 h-5" />
        {error}
      </div>
    )
  }

  if (!streamUrl) {
    return (
      <div className="text-surface-400 text-sm flex items-center gap-2">
        <RefreshCw className="w-5 h-5 animate-spin" />
        Loading camera...
      </div>
    )
  }

  return (
    <img
      src={streamUrl}
      alt="Printer camera"
      className="w-full h-full object-contain rounded-lg"
      onError={() => setError('Camera stream unavailable')}
    />
  )
}
